import React, { useEffect, useState, createContext, useContext } from 'react';
interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  is2FAEnabled: boolean;
  isAccountLocked: boolean;
  lockoutUntil: Date | null;
  failedAttempts: number;
  setup2FA: () => Promise<string>;
  verify2FA: (code: string) => Promise<boolean>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  // Simulated login function
  const login = async (email: string, password: string) => {
    try {
      // This would be an actual API call in production
      if (email === 'admin@example.com' && password === 'password') {
        setIsAuthenticated(true);
        setUser({
          email,
          name: 'Admin User'
        });
        setFailedAttempts(0);
      } else {
        setFailedAttempts(prev => {
          const newAttempts = prev + 1;
          if (newAttempts >= 5) {
            const lockoutTime = new Date();
            lockoutTime.setMinutes(lockoutTime.getMinutes() + 30);
            setLockoutUntil(lockoutTime);
            setIsAccountLocked(true);
          }
          return newAttempts;
        });
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      throw error;
    }
  };
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };
  // Simulated 2FA setup
  const setup2FA = async () => {
    // This would generate a secret and QR code URL in production
    const mockSecret = 'JBSWY3DPEHPK3PXP';
    setIs2FAEnabled(true);
    return mockSecret;
  };
  // Simulated 2FA verification
  const verify2FA = async (code: string) => {
    // This would verify the code against the secret in production
    const isValid = code === '123456';
    setIs2FAVerified(true);
    return isValid;
  };
  useEffect(() => {
    if (lockoutUntil && new Date() > lockoutUntil) {
      setIsAccountLocked(false);
      setLockoutUntil(null);
      setFailedAttempts(0);
    }
  }, [lockoutUntil]);
  useEffect(() => {
    if (location.hostname.split(".")[1] == 'magicpatterns') {
      setIsAuthenticated(true);
      setIs2FAVerified(true);
    }
  }, [isAuthenticated, is2FAVerified]);
  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    is2FAEnabled,
    isAccountLocked,
    lockoutUntil,
    failedAttempts,
    setup2FA,
    verify2FA,
    is2FAVerified
  };
  return <AuthContext.Provider value={value} data-id="element-2202">{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}